import { Table } from '@tanstack/react-table';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import type { InvestmentProgrammeDocumentRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

export const createBulkActions = ({
	tDocuments,
	tMipManage,
	bulkDownloadDocuments,
	bulkExportDocuments,
	bulkReissueDocuments,
	bulkRevokeDocuments,
	bulkSignDocuments,
	bulkAssignCounterparty,
	bulkClaimOnBehalf,
	bulkInviteAuthorisedSignatory,
	bulkRemindSigners,
	bulkApplyDate,
	showRemindSigners,
	shouldDisableActions: disableActions,
	onRevokeSimpleActionSelected,
	onReissueActionSelected,
	onSignActionSelected,
	onAssignCounterpartySelected,
	onClaimOnBehalfSelected,
	onInviteAuthorisedSignatorySelected,
	onRemindSignersSelected,
	onApplyDateSelected,
	shouldShowBulkSign,
}: {
	tDocuments: (key: string) => string;
	tMipManage: (key: string) => string;
	bulkDownloadDocuments: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkExportDocuments: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkReissueDocuments: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkRevokeDocuments: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkSignDocuments?: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkAssignCounterparty: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkClaimOnBehalf: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkInviteAuthorisedSignatory: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkRemindSigners: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	bulkApplyDate: (table: Table<InvestmentProgrammeDocumentRes>) => void;
	showRemindSigners: boolean;
	shouldDisableActions: boolean;
	shouldShowBulkSign?: boolean;
	onRevokeActionSelected?: () => void;
	onRevokeSimpleActionSelected?: () => void;
	onReissueActionSelected?: () => void;
	onSignActionSelected?: () => void;
	onAssignCounterpartySelected?: () => void;
	onClaimOnBehalfSelected?: () => void;
	onInviteAuthorisedSignatorySelected?: () => void;
	onRemindSignersSelected?: () => void;
	onApplyDateSelected?: () => void;
}): BulkActionGroup<InvestmentProgrammeDocumentRes> => {
	const primary: BulkAction<InvestmentProgrammeDocumentRes>[] = [];
	const secondary: BulkAction<InvestmentProgrammeDocumentRes>[] = [];
	const special: BulkAction<InvestmentProgrammeDocumentRes>[] = [];

	primary.push(
		{
			key: 'download',
			label: tDocuments('bulk_actions_menu.download'),
			disabled: disableActions,
			onExecute: (table) => bulkDownloadDocuments(table),
		},
		{
			key: 'export',
			label: tDocuments('bulk_actions_menu.export'),
			disabled: disableActions,
			executeImmediately: true,
			onExecute: (table) => bulkExportDocuments(table),
		},
		{
			key: 'assign-counterparty',
			label: tMipManage('documents_page.bulk_actions_menu.assign_counterparty'),
			dataTestId: TEST_ELEMENT_IDS.BULK_ACTIONS_MENU_ASSIGN_COUNTERPARTY,
			disabled: disableActions,
			onSelect: () => {
				onAssignCounterpartySelected?.();
			},
			onExecute: (table) => bulkAssignCounterparty(table),
		},
		// eID-Easy eligibility is enforced server-side (the invitableAuthorisedSignatory
		// filter + the invite service), so the action is always offered; on non-eID-Easy
		// programmes clicking it resolves to an empty eligible set.
		{
			key: 'invite-authorised-signatory',
			label: tMipManage('documents_page.bulk_actions_menu.assign_authorised_signatory'),
			dataTestId: TEST_ELEMENT_IDS.BULK_ACTIONS_MENU_ASSIGN_AUTHORISED_SIGNATORY,
			disabled: disableActions,
			onSelect: () => {
				onInviteAuthorisedSignatorySelected?.();
			},
			onExecute: (table: Table<InvestmentProgrammeDocumentRes>) =>
				bulkInviteAuthorisedSignatory(table),
		},
		{
			key: 'claim-on-behalf',
			label: tMipManage('documents_page.bulk_actions_menu.claim_on_behalf'),
			disabled: disableActions,
			onSelect: () => {
				onClaimOnBehalfSelected?.();
			},
			onExecute: (table) => bulkClaimOnBehalf(table),
		},
		...(showRemindSigners
			? [
					{
						key: 'remind-signers',
						label: tMipManage('documents_page.bulk_actions_menu.remind_signers'),
						disabled: disableActions,
						onSelect: () => {
							onRemindSignersSelected?.();
						},
						// No executeImmediately: like reissue/revoke/assign, clicking enters
						// row-selection mode and onExecute fires only after the user confirms
						// a selection. Firing immediately would POST an empty selection (400).
						onExecute: (table: Table<InvestmentProgrammeDocumentRes>) =>
							bulkRemindSigners(table),
					},
				]
			: []),
		{
			key: 'reissue',
			label: tDocuments('bulk_actions_menu.reissue'),
			disabled: disableActions,
			onSelect: () => {
				onReissueActionSelected?.();
			},
			onExecute: (table) => bulkReissueDocuments(table),
		},
		{
			key: 'revoke-sdw',
			label: tDocuments('bulk_actions_menu.revoke_documents'),
			disabled: disableActions,
			onSelect: () => {
				onRevokeSimpleActionSelected?.();
			},
			onExecute: (table) => bulkRevokeDocuments(table),
		}
	);

	if (shouldShowBulkSign && bulkSignDocuments) {
		primary.push({
			key: 'sign',
			label: tDocuments('bulk_actions_menu.sign'),
			disabled: disableActions,
			onSelect: () => {
				onSignActionSelected?.();
			},
			onExecute: (table) => bulkSignDocuments(table),
		});
	}

	// Like invite-authorised-signatory: the awaitingPostDating filter enforces eID Easy across the
	// whole programme, so the action is always offered rather than gated on the visible page.
	primary.push({
		key: 'apply-date',
		label: tMipManage('documents_page.bulk_actions_menu.apply_date'),
		dataTestId: TEST_ELEMENT_IDS.BULK_APPLY_DATE_ACTION,
		disabled: disableActions,
		onSelect: () => {
			onApplyDateSelected?.();
		},
		onExecute: (table) => bulkApplyDate(table),
	});

	return { primary, secondary, special };
};
